interface HighestStat {
  key: string;
  team: OpponentBase;
  name: string;
  value: number;
  games_count: number;
}

const getVariationFromData = (data: SeriesStatsBlockMatch): 'dota2' | 'cs' | null => {
  if ('most_picked_maps' in data) {
    return 'cs';
  }
  if ('most_picked_heroes' in data) {
    return 'dota2';
  }
  return null;
};

const getTabFromVariation = (
  variation: 'dota2' | 'cs' | null
): 'maps' | 'heroes' | 'teams' | 'players' => {
  if (variation === 'cs') {
    return 'maps';
  }
  if (variation === 'dota2') {
    return 'heroes';
  }
  return 'teams'; // Default to 'teams' if variation is null
};

const getPlayerHighestStat = (
  key: string,
  playerData: SeriesPlayerWithHighestStat | null
): HighestStat | null => {
  if (!playerData) return null;
  return {
    key,
    team: playerData.player.team,
    name: playerData.player.name,
    value: playerData.value,
    games_count: playerData.games_count
  };
};

const getTeamHighestStat = (
  key: string,
  teamData: SeriesTeamWithHighestStat | null
): HighestStat | null => {
  if (!teamData) return null;
  return {
    key,
    team: teamData.team,
    name: teamData.team.name,
    value: teamData.value,
    games_count: teamData.games_count
  };
};

const getHighestDisplayedStats = (
  data: SeriesStatsBlockMatch,
  tab: 'maps' | 'heroes' | 'teams' | 'players'
): HighestStat[] => {
  if ('most_picked_maps' in data) {
    if (tab === 'players') {
      return [
        getPlayerHighestStat('player_with_highest_kills', data.player_with_highest_kills),
        getPlayerHighestStat(
          'player_with_highest_kills_per_round',
          data.player_with_highest_kills_per_round
        ),
        getPlayerHighestStat('player_with_highest_adr', data.player_with_highest_adr),
        getPlayerHighestStat('player_with_highest_headshots', data.player_with_highest_headshots),
        getPlayerHighestStat(
          'player_with_highest_first_kills',
          data.player_with_highest_first_kills
        )
      ].filter(Boolean) as HighestStat[]; // Filter out null values;;
    }
    if (tab === 'teams') {
      return [
        getTeamHighestStat('team_with_highest_kd_ratio', data.team_with_highest_kd_ratio),
        getTeamHighestStat(
          'team_with_highest_pistol_round_winrate',
          data.team_with_highest_pistol_round_winrate
        ),
        getTeamHighestStat('team_with_highest_utility_kills', data.team_with_highest_utility_kills),
        getTeamHighestStat('team_with_highest_headshots', data.team_with_highest_headshots),
        getTeamHighestStat('team_with_highest_first_kills', data.team_with_highest_first_kills)
      ].filter(Boolean) as HighestStat[]; // Filter out null values;
    }
  } else if ('most_picked_heroes' in data) {
    if (tab === 'players') {
      return [
        getPlayerHighestStat('player_with_highest_kda', data.player_with_highest_kda),
        getPlayerHighestStat('player_with_highest_xp_per_min', data.player_with_highest_xp_per_min),
        getPlayerHighestStat(
          'player_with_highest_gold_per_min',
          data.player_with_highest_gold_per_min
        ),
        getPlayerHighestStat(
          'player_with_highest_hero_damage',
          data.player_with_highest_hero_damage
        ),
        getPlayerHighestStat(
          'player_with_highest_creep_score',
          data.player_with_highest_creep_score
        )
      ].filter(Boolean) as HighestStat[]; // Filter out null values;
    }
    if (tab === 'teams') {
      return [
        getTeamHighestStat('team_with_highest_kills', data.team_with_highest_kills),
        getTeamHighestStat('team_with_highest_xp_per_min', data.team_with_highest_xp_per_min),
        getTeamHighestStat('team_with_highest_gold_per_min', data.team_with_highest_gold_per_min),
        getTeamHighestStat('team_with_highest_hero_damage', data.team_with_highest_hero_damage),
        getTeamHighestStat('team_with_highest_creep_score', data.team_with_highest_creep_score)
      ].filter(Boolean) as HighestStat[]; // Filter out null values;
    }
  }
  return [];
};

export { getVariationFromData, getTabFromVariation, getHighestDisplayedStats };
